import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Aircraft } from "../../../types";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";
import { AircraftSuggestion, ScoreBreakdown } from "./aircraftSuggestionUtils";
import { BulkApplyFloatingAction } from "./BulkApplyFloatingAction";

interface SuggestedAircraftCellProps {
  suggestion: AircraftSuggestion | null;
  currentMapping: Aircraft | null;
  faaManufacturer: string | null;
  faaModel: string | null;
  onApprove: (aircraft: Aircraft) => Promise<void>;
  onManualChange: (aircraft: Aircraft) => Promise<void>;
  onBulkApplyRequest?: (aircraft: Aircraft) => void;
  bulkApplyContext?: {
    aircraft: Aircraft;
    matchCount: number;
    onApply: () => void;
    onDismiss: () => void;
  } | null;
}

/**
 * Enhanced autocomplete cell with intelligent aircraft suggestions.
 *
 * Shows three possible states:
 * 1. No suggestion: Standard empty autocomplete
 * 2. With suggestion: Prefilled autocomplete + checkmark button
 * 3. Already mapped: Shows current mapping
 */
export const SuggestedAircraftCell: React.FC<SuggestedAircraftCellProps> = ({
  suggestion,
  currentMapping,
  faaManufacturer,
  faaModel,
  onApprove,
  onManualChange,
  onBulkApplyRequest,
  bulkApplyContext,
}) => {
  const [isApproving, setIsApproving] = React.useState(false);
  const [flashSuggestion, setFlashSuggestion] = React.useState(false);

  // Track which aircraft ID we've flashed to avoid re-flashing on filter changes
  const flashedAircraftId = React.useRef<string | null>(null);

  // Memoize defaultSelected to prevent new object references on every render
  // This prevents AutocompleteAircraft from thinking the value changed
  const defaultSelected = React.useMemo(() => {
    return currentMapping || suggestion?.suggestedAircraft || undefined;
  }, [currentMapping?.id, suggestion?.suggestedAircraft?.id]);

  // Show suggestion as flashing when it's a new suggestion (not already mapped)
  const shouldFlash =
    !currentMapping && suggestion?.confidence !== null && flashSuggestion;

  // Flash only when we get a NEW aircraft suggestion (different ID than previously flashed)
  React.useEffect(() => {
    const hasSuggestion =
      !currentMapping &&
      suggestion?.confidence !== null &&
      suggestion?.suggestedAircraft?.id;

    if (hasSuggestion) {
      const currentAircraftId = suggestion.suggestedAircraft.id;

      // Only flash if this is a different aircraft than we've already flashed
      if (flashedAircraftId.current !== currentAircraftId) {
        flashedAircraftId.current = currentAircraftId;
        setFlashSuggestion(true);
        const timer = setTimeout(() => setFlashSuggestion(false), 1500);
        return () => clearTimeout(timer);
      }
    } else if (currentMapping?.id) {
      // Reset the flashed ID when suggestion disappears (e.g., row gets mapped)
      flashedAircraftId.current = null;
    }
  }, [
    suggestion?.suggestedAircraft?.id,
    suggestion?.confidence,
    currentMapping?.id,
  ]);

  const handleApprove = async () => {
    if (!suggestion?.suggestedAircraft) return;

    setIsApproving(true);
    try {
      await onApprove(suggestion.suggestedAircraft);
    } catch (error) {
      console.error("Failed to approve suggestion:", error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleManualChange = async (aircraft: Aircraft) => {
    if (!aircraft) return;
    await onManualChange(aircraft);
    // Trigger bulk apply request when aircraft is selected
    if (onBulkApplyRequest) {
      onBulkApplyRequest(aircraft);
    }
  };

  // Determine checkmark color based on confidence
  const getCheckmarkColor = (): "success" | "warning" => {
    if (suggestion?.confidence === "high") return "success";
    if (suggestion?.confidence === "medium") return "warning";
    return "success"; // fallback
  };

  // Calculate visual diff between FAA model and suggested aircraft model
  interface DiffSegment {
    text: string;
    type: "match" | "case-diff" | "different";
  }

  const calculateModelDiff = (
    faaModel: string | null,
    aircraftModel: string | null
  ): DiffSegment[] => {
    if (!faaModel || !aircraftModel) return [];

    const segments: DiffSegment[] = [];
    const maxLen = Math.max(faaModel.length, aircraftModel.length);

    for (let i = 0; i < maxLen; i++) {
      const faaChar = faaModel[i] || "";
      const aircraftChar = aircraftModel[i] || "";

      if (faaChar === aircraftChar) {
        segments.push({ text: faaChar, type: "match" });
      } else if (faaChar.toUpperCase() === aircraftChar.toUpperCase()) {
        segments.push({ text: aircraftChar, type: "case-diff" });
      } else {
        segments.push({ text: aircraftChar, type: "different" });
      }
    }

    return segments;
  };

  const getDiffColor = (type: DiffSegment["type"]): string => {
    switch (type) {
      case "match":
        return "inherit";
      case "case-diff":
        return "warning.main";
      case "different":
        return "error.main";
    }
  };

  // Get human-readable match confidence reason
  const getMatchReason = (breakdown?: ScoreBreakdown): string => {
    if (!breakdown) return "";

    switch (breakdown.method) {
      case "csv-verified":
        return breakdown.matchedHistoricalString
          ? `This FAA string "${breakdown.matchedHistoricalString}" was previously mapped to this aircraft`
          : "Found in verified database mappings";
      case "historical":
        return breakdown.matchedHistoricalString
          ? `Similar to previously mapped: "${breakdown.matchedHistoricalString}"`
          : "Based on historical mapping patterns";
      case "combined":
        return "Match found using both database history and fuzzy text matching";
      case "traditional":
        return "Match found using fuzzy text matching of manufacturer and model";
      default:
        return "Unknown match method";
    }
  };

  // Determine detailed tooltip content with score breakdown
  const getTooltipContent = (): React.ReactNode => {
    if (!suggestion || suggestion.confidence === null) return "";

    const { score, confidence, breakdown, suggestedAircraft } = suggestion;
    const confidenceLabel = confidence === "high" ? "High" : "Medium";
    const borderColor = confidence === "high" ? "success.main" : "warning.main";

    return (
      <Box sx={{ p: 1.5, maxWidth: 500 }}>
        {/* HERO SECTION: Aircraft Image + Name + Confidence Badge */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          {/* Aircraft Image Thumbnail */}
          <Box
            component="img"
            src={`/${suggestedAircraft?.id}.png`}
            alt={`${suggestedAircraft?.make} ${suggestedAircraft?.model}`}
            sx={{
              width: 80,
              height: 80,
              border: 2,
              borderColor: borderColor,
              bgcolor: "background.default",
              borderRadius: 1,
              objectFit: "cover",
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = "none";
            }}
          />

          {/* Aircraft Name + Confidence */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}
            >
              {suggestedAircraft?.make} {suggestedAircraft?.model}
            </Typography>
            <Chip
              label={`${confidenceLabel} confidence`}
              size="small"
              color={confidence === "high" ? "success" : "warning"}
              sx={{ fontWeight: 600 }}
            />
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              Score: {score.toFixed(1)}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* MATCH DETAILS SECTION */}
        <Box sx={{ bgcolor: "action.hover", p: 1.5, borderRadius: 1, mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 1,
              color: "text.secondary",
            }}
          >
            Match Details
          </Typography>

          <Stack spacing={1.5}>
            {/* FAA Input */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                FAA Input (what you're mapping):
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                }}
              >
                {faaManufacturer || "—"} / {faaModel || "—"}
              </Typography>
            </Box>

            {/* Suggested Aircraft */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                Suggested Aircraft:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "success.main",
                  fontSize: "0.875rem",
                }}
              >
                {suggestedAircraft?.make} {suggestedAircraft?.model}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                Score: {score.toFixed(1)} ({confidenceLabel} confidence)
              </Typography>

              {/* Visual Model Comparison */}
              {faaModel && suggestedAircraft?.model && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      fontSize: "0.65rem",
                      mb: 0.5,
                    }}
                  >
                    Model Comparison:
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        minWidth: 50,
                      }}
                    >
                      FAA:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {faaModel}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        minWidth: 50,
                      }}
                    >
                      AirPlx:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {calculateModelDiff(
                        faaModel,
                        suggestedAircraft.model
                      ).map((seg, idx) => (
                        <Box
                          key={idx}
                          component="span"
                          sx={{
                            color: getDiffColor(seg.type),
                            fontWeight: seg.type === "different" ? 700 : 400,
                            textDecoration:
                              seg.type === "different" ? "underline" : "none",
                          }}
                        >
                          {seg.text}
                        </Box>
                      ))}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.6rem", color: "success.main" }}
                    >
                      ✓ match
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.6rem", color: "warning.main" }}
                    >
                      ~ case
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.6rem", color: "error.main" }}
                    >
                      ✗ different
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Match Reason */}
            {breakdown && (
              <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    fontSize: "0.7rem",
                    mb: 0.5,
                  }}
                >
                  Why this match?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                >
                  {getMatchReason(breakdown)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* SIMPLIFIED MATCH QUALITY */}
        {breakdown && (
          <>
            <Typography
              variant="caption"
              sx={{
                mb: 0.5,
                color: "text.secondary",
                display: "block",
                fontSize: "0.7rem",
              }}
            >
              Match Quality Details:
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 2, my: 0.5, fontSize: "0.7rem", "& li": { mb: 0.25 } }}
            >
              {breakdown.method === "csv-verified" && (
                <li>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "success.dark",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    ✓ Verified exact match
                  </Typography>
                </li>
              )}
              <li>
                Manufacturer:{" "}
                {breakdown.manufacturerScore >= 0.9
                  ? `${(breakdown.manufacturerScore * 100).toFixed(0)}% match ✓`
                  : breakdown.manufacturerScore >= 0.7
                  ? `${(breakdown.manufacturerScore * 100).toFixed(0)}% match ~`
                  : "no match ✗"}
              </li>
              <li>
                Model:{" "}
                {breakdown.modelScore === Infinity
                  ? "no direct match"
                  : `score ${breakdown.modelScore.toFixed(1)}`}
              </li>
            </Box>
          </>
        )}

        {/* ALTERNATIVE MATCHES SECTION */}
        {suggestion.alternatives && suggestion.alternatives.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="caption"
              sx={{
                mb: 1,
                color: "text.secondary",
                display: "block",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            >
              Other Possible Matches:
            </Typography>
            <Stack spacing={0.75}>
              {suggestion.alternatives.map((alt) => (
                <Box
                  key={alt.aircraft.id}
                  sx={{
                    p: 1,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.selected",
                      borderColor: "primary.main",
                    },
                  }}
                  onClick={() => onManualChange(alt.aircraft)}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      component="img"
                      src={`/${alt.aircraft.id}.png`}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 0.5,
                        objectFit: "cover",
                      }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      >
                        {alt.aircraft.make} {alt.aircraft.model}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: "0.65rem" }}
                      >
                        Score: {alt.score.toFixed(1)} • {alt.reason}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: "block",
                fontSize: "0.6rem",
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              Click an alternative to select it
            </Typography>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* INSTRUCTIONS SECTION */}
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          Click checkmark to approve, or manually select if incorrect
        </Typography>
      </Box>
    );
  };

  // Show checkmark button only if there's a suggestion and no current mapping
  const showCheckmark = !currentMapping && suggestion?.confidence !== null;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AutocompleteAircraft
          label=""
          defaultSelected={defaultSelected}
          shouldFlash={shouldFlash}
          onSelect={handleManualChange}
        />
      </Box>

      {showCheckmark && (
        <Tooltip
          title={getTooltipContent()}
          placement="left"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 500,
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 3,
              },
            },
          }}
        >
          <IconButton
            size="small"
            color={getCheckmarkColor()}
            onClick={handleApprove}
            disabled={isApproving}
            sx={{
              flexShrink: 0,
              "&:hover": {
                transform: "scale(1.1)",
              },
              transition: "transform 0.2s",
            }}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Bulk Apply Floating Action */}
      {bulkApplyContext && (
        <BulkApplyFloatingAction
          aircraftName={`${bulkApplyContext.aircraft.make} ${bulkApplyContext.aircraft.model}`}
          matchCount={bulkApplyContext.matchCount}
          onApply={bulkApplyContext.onApply}
          onDismiss={bulkApplyContext.onDismiss}
        />
      )}
    </Box>
  );
};
